// /anniversaries/:id/edit — edit page (W3.1 §5.3).
//
// Mirrors new.tsx but pre-fills from the detail query.
//
// W56.0 J (ADR-0201): the 危險區 button no longer opens a confirm dialog.
// A soft delete has a **working 30-day restore window**, so a confirmation box
// was a second interception in front of an exit that already existed — and the
// one it asked for was 「撕下」, a design metaphor rather than something a
// person would say. The delete is immediate and the `UndoBar` is the way back.

import { useEffect, useRef, useState } from "react";
import { useJsonDraft } from "@/lib/hooks/useDraft";
import { useNavigate, useParams } from "react-router-dom";

import { PageHero } from "@/components/primitives/PageHero";
import { PageBottomActionBar } from "@/components/system/PageBottomActionBar";
import { useHidePillNav } from "@/components/system/BottomChrome";
import { FormErrorBanner } from "@/components/auth/FormField";
import { LoadingState } from "@/components/primitives/LoadingState";
import { ErrorState } from "@/components/primitives/ErrorState";
import { PageContainer, SectionStack } from "@/components/layout";
import { UndoBar } from "@/components/patterns/UndoBar";
import { useUndoBar } from "@/hooks/useUndoBar";
import { CategoryPicker } from "../components/CategoryPicker";
import { ReminderDaysPicker } from "../components/ReminderDaysPicker";
import {
  useAnniversaryDetail,
  useDeleteAnniversary,
  useRestoreAnniversary,
  useUpdateAnniversary,
} from "@/lib/hooks/anniversary";
import {
  anniversaryUpdateSchema,
} from "@/lib/validation/anniversary";
import { ApiError } from "@/lib/api/pair";
import { useToast } from "@/components/ui/toast";
import { queryKeys } from "@/lib/queries/keys";
import { ANNIVERSARY_RESTORE_DAYS } from "@/lib/retentionWindows";
import { useQueryClient } from "@tanstack/react-query";

export function EditAnniversaryPage() {
  const params = useParams<{ id: string }>();
  const id = params.id ?? "";
  const nav = useNavigate();
  const qc = useQueryClient();
  const { push: pushToast } = useToast();
  // W35.6: this page docks the submit action, so it owns the bottom edge and
  // the PillNav steps aside (see BottomChrome).
  useHidePillNav();
  const detailQuery = useAnniversaryDetail(id);
  const updateMut = useUpdateAnniversary();
  const deleteMut = useDeleteAnniversary();
  const restoreMut = useRestoreAnniversary();

  // W56.0 J (ADR-0201) — the undo window. `onExpire` is what happens when
  // the 8s bar closes without an undo: the record stays deleted and the page
  // goes back to the list, because the edit form is for a record that no
  // longer exists.
  const undo = useUndoBar<{ id: string; title: string }>({
    onExpire: () => nav("/anniversaries"),
  });

  // W39.76 — found by `composeSurfacesKeepDrafts.test.ts`, not by a probe: this
  // is the same form as `/anniversaries/new` and it was never measured. The
  // guard's whole reason for existing is that the surface list is never finished
  // by enumerating it by hand.
  const formDraft = useJsonDraft(`anniversary-edit:${id}`, {
    title: "",
    description: "",
    customLabel: "",
  });
  const [title, setTitle] = useState(formDraft.value.title);
  const [description, setDescription] = useState(formDraft.value.description);
  const [date, setDate] = useState("");
  const [category, setCategory] = useState("anniversary");
  const [customLabel, setCustomLabel] = useState(formDraft.value.customLabel);
  const [isRecurring, setIsRecurring] = useState(true);
  const [reminderDays, setReminderDays] = useState<number[]>([]);
  const [reminderTime, setReminderTime] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const loaded = detailQuery.data;
  // W56.0 J — the record this page just soft-deleted will make the detail
  // query **404** on its next refetch. react-query keeps the last good row on
  // a background error, but that is the cache's error policy, not a contract,
  // and the page must not become an error screen while the undo window is
  // still open. Keep the row ourselves.
  const lastGoodRef = useRef(loaded);
  if (loaded) lastGoodRef.current = loaded;
  const anniversary = loaded ?? lastGoodRef.current;

  useEffect(() => {
    if (!anniversary) return;
    // W39.76 — a draft outranks the loaded record. This effect runs when the
    // query resolves, which is *after* mount, so seeding unconditionally would
    // read the draft and throw it away in the same breath — the same defect the
    // onboarding profile had. A draft only exists when someone was interrupted.
    setTitle(formDraft.value.title || anniversary.title);
    setDescription(formDraft.value.description || anniversary.description || "");
    setDate(anniversary.date);
    setCategory(anniversary.category);
    setCustomLabel(formDraft.value.customLabel || anniversary.custom_category_label || "");
    setIsRecurring(anniversary.is_recurring);
    setReminderDays(anniversary.reminder_days ?? []);
    setReminderTime(anniversary.reminder_time ?? null);
    // `formDraft.value` is read once, in the hook's lazy initialiser.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anniversary]);

  if (detailQuery.isLoading) {
    return <LoadingState count={3} shape="card" />;
  }
  // W54.0 B2 — a failed read is not 「找不到這個紀念日」. A 500 leaves `data`
  // undefined, so the branch below used to catch it and tell the person the
  // anniversary does not exist. It does exist; we could not read it.
  //
  // W56.0 J (ADR-0201) — **unless we deleted it ourselves.** The refetch that
  // follows our own delete 404s, and replacing the page with an error screen
  // at that moment would unmount the undo bar and take the way back with it.
  if (detailQuery.isError && !undo.item) {
    return <ErrorState onRetry={() => void detailQuery.refetch()} />;
  }
  if (!anniversary) {
    return (
      <div className="p-5">
        <p className="text-body-sm text-ink-2">找不到這個紀念日。</p>
      </div>
    );
  }

  const isAnchor = anniversary.is_anchor_anniversary;

  const submit = async () => {
    setFormError(null);
    const parsed = anniversaryUpdateSchema.safeParse({
      title: title.trim(),
      description: description.trim() || null,
      date: isAnchor ? undefined : date, // anchor locks date
      category,
      custom_category_label: category === "custom" ? customLabel.trim() || null : null,
      is_recurring: isAnchor ? true : isRecurring,
      reminder_days: reminderDays,
      reminder_time: reminderTime,
    });
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? "請檢查欄位");
      return;
    }
    try {
      await updateMut.mutateAsync({ id, payload: parsed.data });
      // W39.76 — cleared only once the update is on the server; this page
      // navigates away, and clearing on unmount is the thing that would make the
      // draft pointless.
      formDraft.clear();
      pushToast({ variant: "success", message: "已更新" });
      void qc.invalidateQueries({ queryKey: queryKeys.anniversary.detail(id) });
      nav(`/anniversaries/${id}`);
    } catch (err) {
      if (err instanceof ApiError) setFormError(err.message);
      else setFormError("更新失敗");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMut.mutateAsync({ id, confirm: isAnchor });
      // W56.0 J (ADR-0201) — immediate + undo. The old flow asked 「撕下」
      // and then navigated to the list, so the 30-day restore window had no
      // way back from the screen the user landed on. Now the page stays, the
      // bar opens, and the expiry is what sends them to the list.
      void qc.invalidateQueries({ queryKey: queryKeys.anniversary.all });
      undo.begin({ id, title: anniversary.title });
    } catch (err) {
      if (err instanceof ApiError) setFormError(err.message);
      else setFormError("刪除失敗");
    }
  };

  return (
    <div data-testid="edit-anniversary-page" className="flex flex-col gap-4 pb-40">
      <PageHero
        // W52.0 D-9 — the trailing arrow becomes the trail's parent segment, so
        // the crumb carries the edit's own id rather than a bare `/anniversaries`
        // that would drop the person onto the list instead of the record they
        // came from.
        crumbs={[
          { to: `/anniversaries/${id}`, label: "紀念日", testId: "anniversary-edit-back" },
          { label: "編輯" },
        ]}
        eyebrow="編輯"
        title="編輯紀念日"
        subtitle={anniversary.title}
      />

      <FormErrorBanner message={formError} />

      {/* W50.0 B-2 — the same `PageContainer` + `SectionStack` composition
          `/anniversaries/new` already uses, so the two forms share one
          vertical rhythm instead of each carrying its own. */}
      <PageContainer width="default" vertical="loose">
        <SectionStack density="default">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="ann-title" className="font-label text-ink-2">
            標題
          </label>
          <input
            id="ann-title"
            type="text"
            maxLength={100}
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              formDraft.save({ title: e.target.value, description, customLabel });
            }}
            data-testid="ann-title"
            className="h-12 rounded-md border border-hairline bg-paper px-3 text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="ann-date" className="font-label text-ink-2">
            日期 {isAnchor ? "(鎖定)" : ""}
          </label>
          <input
            id="ann-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            disabled={isAnchor}
            data-testid="ann-date"
            className="h-12 rounded-md border border-hairline bg-paper px-3 text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
          />
          {isAnchor ? (
            <p className="text-meta text-ink-3">
              交往紀念日的日期由配對設定決定 (mirror), 不可手動更改
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="font-label text-ink-2">分類</span>
          <CategoryPicker
            value={category}
            customLabel={customLabel}
            onCategoryChange={setCategory}
            onCustomLabelChange={setCustomLabel}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="ann-description" className="font-label text-ink-2">
            描述 (可選)
          </label>
          <textarea
            id="ann-description"
            maxLength={500}
            rows={4}
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              formDraft.save({ title, description: e.target.value, customLabel });
            }}
            data-testid="ann-description"
            className="rounded-md border border-hairline bg-paper px-3 py-2 text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2"
          />
          <p className="text-meta text-ink-3 self-end tabular-nums">
            {description.length} / 500
          </p>
        </div>

        {/* R0 (W39.0): the tap target is this `<label>`, not the 16×16 box —
            see the same fix in `new.tsx`. */}
        <label className="flex min-h-11 w-full items-center gap-3">
          <input
            type="checkbox"
            checked={isRecurring || isAnchor}
            onChange={(e) => setIsRecurring(e.target.checked)}
            disabled={isAnchor}
            data-testid="ann-recurring"
            className="h-4 w-4 rounded border-hairline text-you-strong disabled:opacity-60 disabled:cursor-not-allowed"
          />
          <span className="text-body-sm text-ink-1">每年重複 {isAnchor ? "(鎖定)" : ""}</span>
        </label>

        <div className="flex flex-col gap-1.5">
          <span className="font-label text-ink-2">提醒天數</span>
          <ReminderDaysPicker
            value={reminderDays}
            reminderTime={reminderTime}
            onChange={setReminderDays}
            onTimeChange={setReminderTime}
          />
        </div>

        <button
          type="button"
          onClick={() => void handleDelete()}
          disabled={deleteMut.isPending}
          data-testid="anniversary-delete"
          className="self-start h-11 px-4 rounded-md border border-partner/40 text-partner-strong text-body-sm font-semibold hover:bg-partner-tint press disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {deleteMut.isPending ? "刪除中…" : "刪除此紀念日"}
        </button>
        </SectionStack>
      </PageContainer>

      <PageBottomActionBar
        primary={{
          label: updateMut.isPending ? "儲存中…" : "儲存",
          onClick: () => void submit(),
          loading: updateMut.isPending,
          testId: "anniversary-save",
        }}
        // W35.6: this page was the de-facto reference for the row; it keeps
        // the same composition, now expressed through the shared `back` prop.
        back={{
          label: "取消",
          onClick: () => nav(`/anniversaries/${id}`),
          testId: "anniversary-cancel",
        }}
        testId="anniversary-bottom-bar"
      />

      {/*
        W56.0 J (ADR-0201) — the undo bar replaces the confirm dialog.
        It is `fixed` (never in the flow) so showing it moves nothing, and
        docked 108px up so the three-island pill does not sit on the message.
      */}
      {undo.item ? (
        <div
          data-testid="anniversary-edit-undo-slot"
          className="pointer-events-none fixed inset-x-0 bottom-[108px] z-30 flex justify-center px-4"
        >
          <div className="pointer-events-auto relative w-full max-w-md">
            <UndoBar
              open
              message={`已刪除「${undo.item.title}」，${ANNIVERSARY_RESTORE_DAYS} 天內可還原`}
              onUndo={() =>
                restoreMut.mutate(undo.item!.id, {
                  onSuccess: () => undo.clear(),
                })
              }
              className="inset-x-0"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}